import { Hooks } from '#packages/apilib';
import { createLayoutMoveApi_structure } from './createLayoutMoveApi_structure';
import { createLayoutMoveInteractionApi } from './createLayoutMoveInteractionApi';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, Point } from 'types/documentServices';
import type { LayoutGetApi } from '../layoutGetApi';
import type {
  LayoutMoveByOptions,
  LayoutMoveByParams,
  LayoutMoveHooks,
  LayoutMoveToOptions,
} from './types';
import type { LayoutApiByStructure } from '../createLayoutApi_structure';
import type { LayoutMeasureApi } from '../createLayoutMeasureApi';
import type { HistoryApi } from '../createHistoryApi';
import type { CompPointersApi } from '#packages/compPointers';

export function createLayoutMoveApi({
  editorAPI,
  layoutGetApi,
  layoutApi_structure,
  layoutMeasureApi,
  historyApi,
}: {
  editorAPI: EditorAPI;
  layoutGetApi: LayoutGetApi;
  layoutApi_structure: LayoutApiByStructure;
  layoutMeasureApi: LayoutMeasureApi;
  historyApi: HistoryApi;
  compPointersApi: CompPointersApi;
}) {
  const layoutMoveHooks: LayoutMoveHooks = {
    moveByInterceptor: Hooks.createInterceptorHook({
      isCancelAllowed: true,
    }),
  };
  const layoutMoveInteractionApi = createLayoutMoveInteractionApi({
    editorAPI,
    layoutGetApi,
  });
  const layoutMoveApi_structure = createLayoutMoveApi_structure({
    editorAPI,
    historyApi,
    layoutApi_structure,
    layoutGetApi,
  });

  function canMoveByX(compRef: CompRef) {
    return editorAPI.components.is.horizontallyMovable(compRef);
  }

  function canMoveByY(compRef: CompRef) {
    return editorAPI.components.is.verticallyMovable(compRef);
  }

  function canMoveBy(compRef: CompRef, { deltaX, deltaY }: LayoutMoveByParams) {
    const hasDeltaX = typeof deltaX === 'number' && deltaX !== 0;
    const hasDeltaY = typeof deltaY === 'number' && deltaY !== 0;

    if (
      (!hasDeltaX && !hasDeltaY) ||
      (hasDeltaX && !canMoveByX(compRef)) ||
      (hasDeltaY && !canMoveByY(compRef))
    ) {
      return false;
    }

    return true;
  }

  layoutMoveHooks.moveByInterceptor.tap(
    layoutMoveInteractionApi.moveByInterceptor,
  );

  async function moveBy(
    compRefOrRefs: CompRef | CompRef[],
    params: LayoutMoveByParams,
    options: LayoutMoveByOptions = {},
  ) {
    const compRefs = Array.isArray(compRefOrRefs)
      ? editorAPI.components.getComponentsWhichDontHaveAncestorsInTheArray(
          compRefOrRefs,
        )
      : [compRefOrRefs];

    if (
      compRefs.length > 0 &&
      compRefs.every((compRef) => canMoveBy(compRef, params))
    ) {
      // TODO: why do we need to wait? To be sure that previous changes are applied?
      await editorAPI.dsActions.waitForChangesAppliedAsync();

      const interceptionResult = layoutMoveHooks.moveByInterceptor.intercept({
        compRefs,
        params,
      });

      if (interceptionResult.isCanceled) {
        return;
      }

      layoutMoveApi_structure.moveBy(compRefs, params, options);
    }
  }

  async function moveTo(
    compRef: CompRef | CompRef[],
    compPosition: Partial<Point>,
    options: LayoutMoveToOptions = {},
  ) {
    layoutApi_structure.update(
      compRef,
      compPosition,
      options?.dontAddToUndoRedoStack,
    );
  }

  /**
   * Move component to a different position based on bounding box coordinates.
   * Bounding box layout is different when component is rotated.
   */
  async function moveToBoundingCoordinates(
    compRef: CompRef | CompRef[],
    compPositionBounding: Partial<Point>,
    options: LayoutMoveToOptions = {},
  ) {
    const boundingLayout = layoutMeasureApi.getBoundingLayout(compRef);
    const normalLayout = layoutGetApi.get_position(compRef);

    const compPosition = {} as Partial<Point>;
    if (compPositionBounding.x) {
      compPosition.x =
        compPositionBounding.x + (normalLayout.x - boundingLayout.x);
    }
    if (compPositionBounding.y) {
      compPosition.y =
        compPositionBounding.y + (normalLayout.y - boundingLayout.y);
    }

    return moveTo(compRef, compPosition, options);
  }

  async function moveToAndPush(compRef: CompRef, compPosition: Partial<Point>) {
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.dsActions.components.layout.updateAndPushStart(compRef);
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.dsActions.components.layout.updateAndPushUpdate(
      compRef,
      compPosition,
    );
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.dsActions.components.layout.updateAndPushEnd(compRef);
  }

  return {
    hooks: layoutMoveHooks,
    moveBy,
    moveTo,
    moveToBoundingCoordinates,
    moveToAndPush,
    moveByAndPushTransition: layoutMoveApi_structure.moveByAndPushTransition,
    canMoveBy,
  };
}

export type LayoutMoveApi = ReturnType<typeof createLayoutMoveApi>;
